import React, { useContext, useEffect, useState } from 'react'
import { AuthContext, EventsContext, LanguageContext, NotificationContext } from '../context'
import { useNavigate } from 'react-router-dom'
import { AddressInput, DateInput } from './UI'
import { AUTH_PROMPT_TYPE, DATE_TIME_FORMAT } from '../utils/constants'
import moment from 'moment'

function EventModal({ showModal, onClose, event }) {

  const { userData, authUser, isUserAuthenticated, promptAuth } = useContext(AuthContext)
  const { createEvent, updateEvent, deleteEvent } = useContext(EventsContext)
  const { toast, prompt } = useContext(NotificationContext)
  const { t } = useContext(LanguageContext)

  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)

  const [eventId, setEventId] = useState(null)
  const emptyEventData = {
    title: '',
    organiser_id: '',
    organiser_firstname: '',
    organiser_lastname: '',
    address: '',
    start: '',
    end: '',
    notes: '',
  };
  const [eventData, setEventData] = useState(emptyEventData)

  const [formSubmitted, setFormSubmitted] = useState(false)

  const handleClose = () => {
    setEventData({
      ...emptyEventData,
    })
    onClose(false)
  }

  useEffect(() => {
    if (!event) {
      setEventId(null)
      setEventData(emptyEventData);
    } else {
      setEventId(event.id)
      setEventData({ ...event })
    }
  }, [event])

  useEffect(() => {
    if (!userData) return
    const updates = {}
    updates.organiser_firstname = event?.organiser_firstname || userData.firstname;
    updates.organiser_lastname = event?.organiser_lastname || userData.lastname;
    if (Object.keys(updates).length > 0) {
      setEventData(data => ({ ...data, ...updates }))
    }
  }, [userData])

  const handleChange = (key) => (e) => {
    if (key === "start" && !eventData.end) {
      setEventData(data => ({ ...data, start: e.target.value, end: e.target.value }))
      return
    }
    setEventData(data => ({ ...data, [key]: e.target.value }))
  }

  const handleCreateEvent = async () => {
    setLoading(true);
    const createdEvent = await createEvent(eventData)
    setLoading(false);
    handleClose(false);
    setEventData(emptyEventData);
    navigate(`/event/${createdEvent.id}`)
  }

  const handleSubmit = async () => {
    setFormSubmitted(true);

    if (!eventData.title || !eventData.organiser_firstname || !eventData.organiser_lastname || !eventData.address || !eventData.start || !eventData.end) {
      toast({ message: t('generic.complete_all_fields'), variant: 'error' })
      return
    }
    if (!isUserAuthenticated) {
      // open login modal
      promptAuth(AUTH_PROMPT_TYPE.LOGIN, {}, handleCreateEvent)
      return
    }
    handleCreateEvent()
  }

  const handleSubmitUpdate = async () => {
    setFormSubmitted(true);
    if (!eventData.title || !eventData.organiser_firstname || !eventData.organiser_lastname || !eventData.address || !eventData.start || !eventData.end) {
      toast({ message: t('generic.complete_all_fields'), variant: 'error' })
      return
    }
    setLoading(true);
    await updateEvent(eventId, eventData)
    setLoading(false);
    handleClose(false)
    toast({ message: t('generic.changes_saved'), variant: 'success' })
  }

  const handleDelete = async (e) => {
    e.preventDefault()
    const response = await prompt({ message: t('event.delete_confirm'), buttons: [{ label: t('generic.cancel'), value: 'cancel', variant: 'primary-border' }, { label: t('generic.confirm'), value: 'confirm', variant: 'primary' }] })
    if (response !== 'confirm') return
    setLoading(true);
    await deleteEvent(eventId)
    setLoading(false);
    handleClose(false);
    //todo: we might need to reset eventData
    navigate(`/my-events`);
  }

  const convertDatetimeStringToDate = (dateString) => {
    if (dateString) {
      const dateMoment = moment(dateString, DATE_TIME_FORMAT)
      return new Date(dateMoment.valueOf())
    }
    return null
  }

  return (
    <div className={`w-full ${showModal ? '' : 'hidden'}`}>
      <div
        className="justify-center items-center z-50 rounded-lg modal-container ">
        <div className="flex items-start justify-between pt-16 pb-9 px-5  ">
          <h3 className="modal-title">
            {eventId ? t('event.edit_title') : t('event.detail_title')}
          </h3>
          <button
            className="p-1 bg-transparent text-black text-3xl absolute top-2 right-4"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {/*body*/}
        <div className="modal-box">

          <div>
            <div className='mb-4'>
              <label className='block mb-2 required'>
                {t('event.name_label')}*
              </label>
              <input type='text' className={formSubmitted && !eventData.title ? '!border-red-500' : ''} value={eventData.title} onChange={handleChange('title')} required id="event-name" name='eventName' autoComplete='on' />
            </div>
            <div className='mb-4'>
              <label className='block mb-2 required'>
                {t('event.organiser_firstname_label')}*
              </label>
              <input type='text' className={formSubmitted && !eventData.organiser_firstname ? '!border-red-500' : ''} value={eventData.organiser_firstname} onChange={handleChange('organiser_firstname')} required id="firstname" name='firstName' autoComplete='on' />
            </div>
            <div className='mb-4'>
              <label className='block mb-2 required'>
                {t('event.organiser_lastname_label')}*
              </label>
              <input type='text' className={formSubmitted && !eventData.organiser_lastname ? '!border-red-500' : ''} value={eventData.organiser_lastname} onChange={handleChange('organiser_lastname')} required id="lastname" name='lastName' autoComplete='on' />
            </div>
            <div className='mb-4'>
              <label className='block mb-2 required'>
                {t('event.address_label')}*
              </label>
              {/* <input type='text' className='border' value={eventData.address} onChange={handleChange('address')} /> */}
              <AddressInput onChange={handleChange('address')} value={eventData.address} className={formSubmitted && !eventData.address ? '!border-red-500' : ''} id="address" />
            </div>
            <div className='mb-4'>
              <label className='block mb-2 required'>
                {t('event.start_label')}*
              </label>
              <DateInput value={eventData.start} onChange={handleChange('start')}
                minDate={new Date()}
                maxDate={convertDatetimeStringToDate(eventData.end)}
                className={formSubmitted && !eventData.start ? '!border-red-500' : ''}
                id="date-start"
              />
            </div>
            <div className='mb-8'>
              <label className='block mb-2 required'>
                {t('event.end_label')}*
              </label>
              <DateInput
                value={eventData.end}
                onChange={handleChange('end')}
                minDate={convertDatetimeStringToDate(!eventData.start || (new Date()) > convertDatetimeStringToDate(eventData.start)) ? new Date() : convertDatetimeStringToDate(eventData.start)}
                className={formSubmitted && !eventData.end ? '!border-red-500' : ''}
                id="date-end"
              />
            </div>
            {!eventId && (
              <button onClick={handleSubmit} className={`mid w-full btn-loading ${loading ? 'running' : ''}`}
                disabled={loading}>
                <div className="loading"></div>
                {t('event.create_button')}
              </button>
            )}

            {/* edit mode */}
            {eventId && eventData.organiser_id === authUser.uid && (
              <>
                <div className='flex flex-row gap-6 mb-8'>
                  <button className='mid light flex' onClick={onClose}>{t('generic.cancel')}</button>
                  <button type='submit' onClick={handleSubmitUpdate} className={`mid flex btn-loading ${loading ? 'running' : ''}`}>
                    <div className="loading"></div>
                    {t('generic.save')}
                  </button>
                </div>
                <button className='delete small' onClick={handleDelete}>
                  {t('event.delete_button')}
                </button>
              </>
            )}
          </div>


        </div>


      </div>
      <div className="fixed inset-0 z-40 modal-bg" onClick={onClose}></div>
    </div >
  )
}

export default EventModal
